// The ready pool (sources-v2 D9): every file source's `<video>`, keyed by source id like its texture.
import { IS_DEV } from '~/lib/constants'
import { detectFps } from '~/lib/videoFps'
import { libraryIdOf } from './ids'
import library from './library'
import { isHeld, onHold } from './textures'

export const WARM_MAX = 3

/** Pure: the unpinned entries to drop — past `max`, wished-for ones kept first, then the most recently staged. */
export const pickEvictions = (entries, { pinned, wished, max = WARM_MAX }) =>
	entries
		.filter(e => !pinned.has(e.id))
		.sort((a, b) => wished.has(b.id) - wished.has(a.id) || b.usedAt - a.usedAt)
		.slice(max)
		.map(e => e.id)

const logger = console.scope('videoPool')
const entries = new Map()
const staged = new Map()
const provided = new Map()
const listeners = new Set()
let wished = new Set()
let audio = null
let muted = false
let box = null
let useClock = 0

const emit = (type, payload) => {
	for (const fn of listeners) fn(type, payload)
}

// In the document, hidden: rVFC and decoding are only reliable for an attached element.
const container = () => {
	if (!box) {
		box = document.createElement('div')
		box.hidden = true
		box.dataset.videoPool = ''
		document.body.appendChild(box)
	}
	return box
}

const pinnedIds = () => {
	const ids = new Set(staged.values())
	for (const id of entries.keys()) if (isHeld(id)) ids.add(id)
	return ids
}

const isPinned = id => pinnedIds().has(id)

const destroy = e => {
	if (entries.get(e.id) === e) entries.delete(e.id)
	const v = e.video
	if (v) {
		v.pause()
		v.removeAttribute('src')
		v.load()
		v.remove()
	}
	e.gain?.disconnect()
	e.source?.disconnect()
	if (e.url) URL.revokeObjectURL(e.url)
}

const evict = () => {
	for (const id of pickEvictions([...entries.values()], { pinned: pinnedIds(), wished })) destroy(entries.get(id))
}

const ensureAudio = () => {
	if (!audio) {
		const ctx = new AudioContext()
		const speakers = ctx.createGain()
		speakers.gain.value = muted ? 0 : 1
		speakers.connect(ctx.destination)
		audio = { ctx, speakers, send: ctx.createMediaStreamDestination() }
	}
	return audio
}

/** Speakers and sender hear a file only while it plays on stage or in a fade. `createMediaElementSource` works once per element, and Chrome's is silent from a muted one. */
const setAudible = (e, on) => {
	if (!e.video) return
	if (!e.gain) {
		if (!on) return
		const { ctx, speakers, send } = ensureAudio()
		try {
			e.source = ctx.createMediaElementSource(e.video)
			e.gain = ctx.createGain()
			e.source.connect(e.gain)
			e.gain.connect(speakers)
			e.gain.connect(send)
			e.video.muted = false
		} catch (err) {
			logger.warn('file audio not routed', e.id, err)
			return
		}
	}
	e.gain.gain.value = on ? 1 : 0
}

/** A suspended context stalls playback of every routed element: `play()` resolves and nothing advances. */
export const resumeAudio = () => {
	if (audio?.ctx.state === 'suspended') audio.ctx.resume().catch(() => {})
}

const openFile = async id => {
	const given = provided.get(id)
	if (given) {
		provided.delete(id)
		return given
	}
	const libId = libraryIdOf(id)
	if (!libId) return null
	const res = await library.open(libId, { request: false, touch: false })
	if (!res) return null
	if (res.needsGesture) return { needsGesture: true }
	// An import's File is named by its OPFS id; the entry holds the real name.
	return { file: res.file, name: res.entry.name, fps: res.entry.fps }
}

const attach = (e, { file, name, fps }) => {
	const video = document.createElement('video')
	// Muted until it is first made audible: a hidden element must load without an autoplay refusal.
	video.muted = true
	video.playsInline = true
	video.preload = 'auto'
	video._frameReady = false
	video._fps = fps ?? null
	video.addEventListener('error', () => {
		const code = video.error?.code
		logger.warn('file failed', e.id, code)
		destroy(e)
		emit('error', { id: e.id, code })
	})
	e.url = URL.createObjectURL(file)
	e.name = name
	e.size = file.size
	e.fps = fps ?? null
	e.video = video
	video.src = e.url
	video.requestVideoFrameCallback(() => {
		video._frameReady = true
	})
	container().appendChild(video)
	if (fps == null)
		detectFps(file)
			.then(({ fps: detected }) => {
				video._fps = e.fps = detected
				emit('fps', { id: e.id })
			})
			.catch(() => {})
}

/** @returns {Promise<{entry}|{needsGesture: true}|null>} null = no such file (or evicted meanwhile) */
const load = id => {
	const found = entries.get(id)
	if (found) return found.loading
	const e = {
		id,
		video: null,
		url: null,
		name: null,
		size: null,
		fps: null,
		usedAt: 0,
		warmAt: null,
		gain: null,
		source: null,
	}
	entries.set(id, e)
	e.loading = openFile(id)
		.catch(err => {
			logger.warn('open failed', id, err)
			return null
		})
		.then(res => {
			if (entries.get(id) !== e) return null
			if (!res?.file) {
				entries.delete(id)
				return res
			}
			attach(e, res)
			return { entry: e }
		})
	return e.loading
}

/** No longer staged or held: paused where it is, silent, and up for eviction. */
const settle = id => {
	const e = entries.get(id)
	if (e && !isPinned(id)) {
		e.video?.pause()
		setAudible(e, false)
	}
	evict()
}

onHold((id, held) => {
	if (!held && entries.has(id)) settle(id)
})

/**
 * Puts a file source on a node (null: none), paused where its playhead is. The previous one pauses unless a fade holds it.
 * @returns {Promise<void>} emits 'lost' `{ id, needsGesture }` when the file does not open without a gesture
 */
export const stage = async (node, id) => {
	const prev = staged.get(node) ?? null
	if (prev === id) return
	if (id) staged.set(node, id)
	else staged.delete(node)
	if (prev) settle(prev)
	emit('stage', { node })
	if (!id) return
	const known = entries.has(id)
	const res = await load(id)
	if (staged.get(node) !== id) return
	if (!res?.entry) {
		staged.delete(node)
		emit('stage', { node })
		emit('lost', { id, needsGesture: !!res?.needsGesture })
		return
	}
	const e = res.entry
	e.usedAt = ++useClock
	e.warmAt = null
	// Never plays it: a file goes on stage paused at its playhead, and only the transport (later a preset's `run`) plays it.
	setAudible(e, true)
	// A file opened before (a preload or an earlier stage) went on stage again without a library open.
	if (known && libraryIdOf(id)) library.touch(libraryIdOf(id)).catch(() => {})
	emit('stage', { node })
	evict()
}

/** Keeps these file sources loaded and paused, in priority order, each at `at` (a preset's start cue) unless staged or held; replaces the previous wish list. */
export const warm = list => {
	const top = list.slice(0, WARM_MAX)
	wished = new Set(top.map(w => w.id))
	for (const { id, at } of top)
		load(id).then(res => {
			const e = res?.entry
			if (e && !isPinned(id) && e.warmAt !== at) {
				e.warmAt = at
				e.video.currentTime = at
			}
			evict()
		})
	evict()
}

/** A just-picked file for `id`, so staging it needs no library read (and works without one: the `<input type=file>` fallback). */
export const provide = (id, { file, fileName }) => {
	if (!entries.has(id)) provided.set(id, { file, name: fileName })
}

/** Unloads a file source that left the library, unless staged or held (eviction takes it later). */
export const drop = id => {
	provided.delete(id)
	if (!isPinned(id) && entries.has(id)) destroy(entries.get(id))
}

/** A file this session can open without the library: picked here, or already loaded. */
export const isKnown = id => entries.has(id) || provided.has(id)

/** The node's staged entry once its element exists, else null. */
export const stagedEntry = node => (entries.get(staged.get(node))?.video ? entries.get(staged.get(node)) : null)

export const stagedId = node => staged.get(node) ?? null

/** Every loaded `{ id, video }`, for the session clock to upload the wanted ones. */
export const loaded = () => [...entries.values()].filter(e => e.video)

export const setMuted = on => {
	muted = on
	if (audio) audio.speakers.gain.value = on ? 0 : 1
}

export const isMuted = () => muted

/** The file sound for the audio sender: one track for every file, so a switch never re-sends it. */
export const audioTrack = () => audio?.send.stream.getAudioTracks()[0] ?? null

/** `fn(type, payload)`: 'stage' `{ node }`, 'lost' `{ id, needsGesture }`, 'error' `{ id, code }`, 'fps' `{ id }`. @returns {() => void} unsubscribe */
export const subscribe = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

if (IS_DEV)
	window.pool = {
		snapshot: () =>
			Object.fromEntries(
				[...entries.values()].map(e => [
					e.id,
					{
						name: e.name,
						loaded: !!e.video,
						frame: !!e.video?._frameReady,
						paused: e.video?.paused ?? null,
						time: e.video?.currentTime ?? null,
						audible: e.gain ? e.gain.gain.value > 0 : false,
						usedAt: e.usedAt,
					},
				])
			),
		staged: () => Object.fromEntries(staged),
		wished: () => [...wished],
	}

if (import.meta.hot)
	import.meta.hot.dispose(() => {
		for (const e of [...entries.values()]) destroy(e)
		audio?.ctx.close().catch(() => {})
		box?.remove()
	})
